import { useRoute } from 'preact-iso';
import { useLocale, useTranslation } from '../i18n';
import { useFetch } from '../hooks/useFetch';
import { api, ApiError } from '../api/client';
import { Skeleton } from '../primitives';
import { Layout } from '../components/Layout';
import { ArticleBody } from '../components/ArticleBody';
import { ArticleGalleryLightbox } from '../components/ArticleGalleryLightbox';
import { ArticleHero } from '../components/ArticleHero';
import { ArticleTocSidebar } from '../components/ArticleTocSidebar';
import { withBase } from '../paths';
import '../styles/article-prose.css';

/**
 * /articles/:id/:slug - single article view. Hero + body + sticky sidebar.
 * The id resolves the article; the trailing slug is a decorative SEO suffix.
 * Body is server-rendered HTML inlined via <c>ArticleBody</c> (the
 * sanitiser ran on save so we can <c>dangerouslySetInnerHTML</c>
 * directly without re-sanitising client-side).
 *
 * <para>404 vs error path: the backend returns 404 for both
 * "no such id" and "id exists but is in a non-public state"
 * (Draft / Scheduled / Hidden). We don't try to distinguish them -
 * leaking the existence of unpublished work to readers is a feature,
 * not a bug.</para>
 */
export function ArticleDetailPage() {
    const t = useTranslation();
    const { apiSet } = useLocale();
    const route = useRoute();
    const id = route.params.id;
    const result = useFetch(
        () =>
            id
                ? api.articles.getById(id, apiSet)
                : Promise.reject(new Error('missing id')),
        [id, apiSet],
    );

    if (result.status === 'loading' || result.status === 'idle') {
        return (
            <Layout current="articles">
                <div class="aspect-[3/1] w-full bg-surface-2" />
                <div class="mx-auto max-w-[1280px] px-[clamp(20px,3.5vw,56px)] py-12">
                    <Skeleton.Line height="3rem" width="60%" />
                    <div class="mt-8 space-y-3">
                        <Skeleton.Line />
                        <Skeleton.Line />
                        <Skeleton.Line width="80%" />
                    </div>
                </div>
            </Layout>
        );
    }

    if (result.status === 'error') {
        const isNotFound =
            (result.error instanceof ApiError && result.error.status === 404) ||
            /HTTP 404|404 /.test(result.error.message);
        return (
            <Layout current="articles">
                <div class="mx-auto max-w-[820px] px-[clamp(20px,3.5vw,56px)] py-24 text-center">
                    <h1 class="font-display text-4xl italic text-fg-primary">
                        {isNotFound ? t('article.notFoundTitle') : t('article.errorTitle')}
                    </h1>
                    <p class="mt-4 text-fg-muted">
                        {isNotFound ? t('article.notFoundBody') : result.error.message}
                    </p>
                    <a
                        href={withBase('/articles')}
                        class="mt-6 inline-block text-accent hover:text-accent-soft"
                    >
                        ← {t('article.backToList')}
                    </a>
                </div>
            </Layout>
        );
    }

    const article = result.data;
    return (
        <Layout current="articles">
            <ArticleHero article={article} />
            <div class="mx-auto max-w-[1280px] px-[clamp(20px,3.5vw,56px)] py-[clamp(40px,5vw,72px)]">
                <div class="grid grid-cols-1 gap-[clamp(40px,5vw,80px)] lg:grid-cols-[1fr_280px]">
                    <article class="min-w-0">
                        <ArticleBody article={article} />
                    </article>
                    <ArticleTocSidebar article={article} />
                </div>
            </div>
            {/* Click-delegated lightbox for :::gallery directives in the
                body. Mounts once per article view; the delegated listener
                only fires for clicks inside .article-gallery elements. */}
            <ArticleGalleryLightbox />
        </Layout>
    );
}
